import { useCallback, useEffect, useState } from 'react';
import {
  providers as loadProviders,
  reconciliation as loadReport,
  runReconciliation,
  type ReconciliationReport,
} from './api';
import type { PaymentsKey, PaymentsTranslate } from './i18n';
import { Table, TableSkeleton, Field, Select, TextInput } from '../../shell/kit';

export interface ReconciliationTabProps {
  readonly t: PaymentsTranslate;
  readonly onError: (message: string) => void;
}

function yesterday(): string {
  return new Date(Date.now() - 86400000).toISOString().slice(0, 10);
}

export function ReconciliationTab({ t, onError }: ReconciliationTabProps) {
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [codes, setCodes] = useState<readonly string[]>([]);
  const [provider, setProvider] = useState('');
  const [day, setDay] = useState(yesterday());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [latest, list] = await Promise.all([loadReport(), loadProviders()]);
      setReport(latest);
      const payment = list.providers.filter((p) => p.kind !== 'fiscal').map((p) => p.code);
      setCodes(payment);
      setProvider((current) => current || (payment[0] ?? ''));
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const findings = report?.findings ?? [];

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h3 className="text-lg font-semibold text-ink">{t('recon.title')}</h3>
        <div className="flex items-end gap-2">
          <Field label={t('recon.provider')}>
            <Select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {codes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('recon.day')}>
            <TextInput type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
          <button
            type="button"
            disabled={busy || !provider}
            className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
            onClick={() => {
              setBusy(true);
              runReconciliation(provider, day)
                .then((r) => setReport(r))
                .catch((err: Error) => onError(err.message))
                .finally(() => setBusy(false));
            }}
          >
            {t('recon.run')}
          </button>
        </div>
      </header>

      {!report ? (
        <p className="text-ink-muted">{t('recon.never')}</p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3 rounded border border-line bg-surface p-4 sm:grid-cols-4">
            <div>
              <dt className="text-xs text-ink-muted">{t('recon.system')}</dt>
              <dd className="text-lg font-semibold">{report.run.systemCount}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('recon.providerCount')}</dt>
              <dd className="text-lg font-semibold">{report.run.providerCount}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('recon.fiscalCount')}</dt>
              <dd className="text-lg font-semibold">{report.run.fiscalCount}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('recon.findings')}</dt>
              <dd
                className={`text-lg font-semibold ${
                  report.run.findingsCount === 0 ? 'text-ok' : 'text-danger'
                }`}
              >
                {report.run.findingsCount}
              </dd>
            </div>
          </dl>

          {loading ? (
            <TableSkeleton rows={5} columns={5} />
          ) : findings.length === 0 ? (
            <p className="text-ok">{t('recon.empty')}</p>
          ) : (
            <>
              <p className="text-sm font-medium text-danger">{t('recon.incident')}</p>
              <div className="overflow-x-auto rounded border border-line bg-surface">
                <Table className="text-left">
                  <thead className="bg-sunken text-xs uppercase text-ink-muted">
                    <tr>
                      <th className="px-3 py-2">{t('recon.kind')}</th>
                      <th className="px-3 py-2">{t('tx.external')}</th>
                      <th className="px-3 py-2">{t('recon.system')}</th>
                      <th className="px-3 py-2">{t('recon.providerCount')}</th>
                      <th className="px-3 py-2">{t('recon.fiscalCount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findings.map((finding) => (
                      <tr key={finding.id} className="border-t border-line">
                        <td className="px-3 py-2">
                          {t(`recon.kind.${finding.kind}` as PaymentsKey)}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{finding.externalId ?? '—'}</td>
                        <td className="px-3 py-2">{finding.systemMinor ?? '—'}</td>
                        <td className="px-3 py-2">{finding.providerMinor ?? '—'}</td>
                        <td className="px-3 py-2">{finding.fiscalMinor ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
